/**
 * Permission guards for NeoTIM route protection
 * TypeScript rewrite of guards.js with bug fixes
 */

import { redirect } from '@tanstack/react-router'
import { usePermissionStore } from '../stores/permissions.store'
import { matchesPermission } from './parser'
import type { PermissionContext } from './types'

/**
 * Get permissions from the parent loader data
 */
async function getPermissionsFromParentLoader(
  parentMatchPromise: Promise<unknown>
): Promise<{
  permissions: readonly string[]
  hasWildcard: boolean
  context: PermissionContext
} | null> {
  try {
    const parentMatch = await parentMatchPromise
    if (!parentMatch || typeof parentMatch !== 'object') {
      return null
    }

    const parent = parentMatch as Record<string, unknown>
    
    // Try to get from loaderData
    if ('loaderData' in parent && parent.loaderData && typeof parent.loaderData === 'object') {
      const loaderData = parent.loaderData as Record<string, unknown>
      
      if ('permissions' in loaderData && Array.isArray(loaderData.permissions)) {
        return {
          permissions: loaderData.permissions as readonly string[],
          hasWildcard: (loaderData.hasWildcard as boolean) ?? false,
          context: (loaderData.permissionContext as PermissionContext) ?? {
            filiale_code: '',
            dept_path: '',
            is_manager: false,
            is_dept_head: false,
            personne_id: '',
            managed_count: 0,
          },
        }
      }
    }

    return null
  } catch {
    return null
  }
}

/**
 * Get permissions from store (fallback)
 */
function getPermissionsFromStore(): {
  permissions: readonly string[]
  hasWildcard: boolean
  context: PermissionContext
} {
  const state = usePermissionStore.getState()
  return {
    permissions: state.permissions,
    hasWildcard: state.hasWildcard,
    context: state.context,
  }
}

/**
 * Get permissions - tries parent loader first, falls back to store
 */
async function getPermissions(
  parentMatchPromise?: Promise<unknown>
): Promise<{
  permissions: readonly string[]
  hasWildcard: boolean
  context: PermissionContext
}> {
  if (parentMatchPromise) {
    const fromLoader = await getPermissionsFromParentLoader(parentMatchPromise)
    if (fromLoader && (fromLoader.permissions.length > 0 || fromLoader.hasWildcard)) {
      return fromLoader
    }
  }

  return getPermissionsFromStore()
}

/**
 * Check if a permission matches
 */
function hasPermission(
  permissions: readonly string[],
  hasWildcard: boolean,
  context: PermissionContext,
  required: string
): boolean {
  if (hasWildcard) return true
  return permissions.some((grant) => matchesPermission(grant, required, context))
}

/**
 * Filter out scope:self grants from permissions array.
 * Used when access requires HR/management level (not plain employee).
 */
function filterOutSelfScope(permissions: readonly string[]): string[] {
  return permissions.filter((p) => !p.includes(':scope:self'))
}

/**
 * Require a single permission - throws redirect if denied
 */
export function requirePermission(
  permission: string,
  options?: { redirectTo?: string }
): (props: { parentMatch?: Promise<unknown> }) => Promise<void> {
  return async ({ parentMatch }: { parentMatch?: Promise<unknown> }) => {
    const { permissions, hasWildcard, context } = await getPermissions(parentMatch)

    if (!hasPermission(permissions, hasWildcard, context, permission)) {
      throw redirect({
        to: options?.redirectTo ?? '/unauthorized',
      })
    }
  }
}

/**
 * Require any of the specified permissions - throws redirect if denied
 */
export function requireAnyPermission(
  permissions: string[],
  options?: { redirectTo?: string }
): (props: { parentMatch?: Promise<unknown> }) => Promise<void> {
  return async ({ parentMatch }: { parentMatch?: Promise<unknown> }) => {
    const perms = await getPermissions(parentMatch)

    const hasAny = permissions.some((p) =>
      hasPermission(perms.permissions, perms.hasWildcard, perms.context, p)
    )

    if (!hasAny) {
      throw redirect({
        to: options?.redirectTo ?? '/unauthorized',
      })
    }
  }
}

/**
 * Require HR management-level access (excludes plain employees with scope:self only).
 * Throws redirect if user only has scope:self grants or no matching grants.
 * 
 * Use for module parent routes (e.g., /rh) to ensure only Admin/DRH/Manager can access,
 * not plain employees who only have self-scoped permissions.
 */
export function requireHRManagementAccess(
  options?: { redirectTo?: string }
): (props: { parentMatch?: Promise<unknown> }) => Promise<void> {
  return async ({ parentMatch }: { parentMatch?: Promise<unknown> }) => {
    const perms = await getPermissions(parentMatch)

    // Admin wildcard always has access
    if (perms.hasWildcard) {
      return
    }

    // Filter out scope:self grants - these are for plain employees only
    const managementPermissions = filterOutSelfScope(perms.permissions)

    // Check if user has any RH management permission (rh.* without :scope:self)
    const hasHRManagement = managementPermissions.some((p) => p.startsWith('rh.'))

    if (!hasHRManagement) {
      throw redirect({
        to: options?.redirectTo ?? '/unauthorized',
      })
    }
  }
}

/**
 * Require all of the specified permissions - throws redirect if denied
 */
export function requireAllPermissions(
  permissions: string[],
  options?: { redirectTo?: string }
): (props: { parentMatch?: Promise<unknown> }) => Promise<void> {
  return async ({ parentMatch }: { parentMatch?: Promise<unknown> }) => {
    const perms = await getPermissions(parentMatch)

    const hasAll = permissions.every((p) =>
      hasPermission(perms.permissions, perms.hasWildcard, perms.context, p)
    )

    if (!hasAll) {
      throw redirect({
        to: options?.redirectTo ?? '/unauthorized',
      })
    }
  }
}

/**
 * Create authentication guard - checks if user is logged in
 */
export function createAuthGuard(options?: {
  redirectToLogin?: string
  redirectIfAuthenticated?: string
}): {
  beforeLoad: () => Promise<void> | void
} {
  return {
    beforeLoad: () => {
      const state = usePermissionStore.getState()
      
      // Check if we have any permissions (user is logged in)
      if (state.permissions.length === 0 && !state.hasWildcard) {
        if (options?.redirectToLogin) {
          throw redirect({ to: options.redirectToLogin })
        }
        // If no redirect specified, just return (allow access check to proceed)
        return
      }

      // If authenticated and redirectIfAuthenticated is set, redirect
      if (options?.redirectIfAuthenticated) {
        throw redirect({ to: options.redirectIfAuthenticated })
      }
    },
  }
}
